import React, { ReactNode, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import { UserCard, UserCardRow } from 'src/pages/ProjectUsersPage/components/UserCard';
import { DeleteUserDialog } from 'src/pages/ProjectUsersPage/components/DeleteUserDialog';
import { GetProjectUsersResponse, ProjectUserContact, ProjectUserOccupation, } from 'src/model/api/v2/data-contracts';
import { useDeleteContactMutation, useModifyContactMutation } from 'src/api/v1/contact-ApiSlice';
import { ContactResponse } from 'src/model/api/contact.model';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { DeleteIcon } from 'src/icons/DeleteIcon';
import { EditIcon } from 'src/icons/EditIcon';
import { getV1ErrorMessage } from 'src/utils/v1ErrorMessage';
import { ts } from 'src/utils/translate';
import { DEFAULT_SPACING } from 'src/theme/style-consts';

type Props = {
    projectId: number;
    users: GetProjectUsersResponse;
    canEdit: boolean;
    inviteSlot?: ReactNode;
};

function contactRows(contact: ProjectUserContact): UserCardRow[] {
    return [
        { label: ts('project_users_first_name', 'First name'), value: contact.first_name },
        { label: ts('project_users_last_name', 'Last name'), value: contact.last_name },
        { label: ts('project_users_email', 'Email address'), value: contact.email },
        { label: ts('project_users_phone', 'Phone number'), value: contact.telephone },
    ];
}

function isAccepted(occupation: ProjectUserOccupation): boolean {
    return occupation.has_accepted_registration_invite && occupation.has_user_accepted_occupation;
}

function pendingTitle(occupation: ProjectUserOccupation): string {
    if (occupation.has_user_accepted_occupation) {
        return '';
    }
    return occupation.has_accepted_registration_invite
        ? ts('project_users_pending_invitation', 'Pending invitation')
        : ts('project_users_waiting_for_registration', 'Waiting for user to register');
}

function rejectNullResponse(response: ContactResponse | null): ContactResponse {
    if (response == null) {
        throw new Error('Contact request was rejected');
    }
    return response;
}

function occupationKey(occupation: ProjectUserOccupation): string {
    return `${occupation.contact.user_id}-${occupation.role_id}`;
}

export const ProjectUserCards = ({ projectId, users, canEdit, inviteSlot }: Props) => {
    const dispatch = useAppDispatch();
    const [modifyContact, { isLoading: isModifying }] = useModifyContactMutation();
    const [deleteContact, { isLoading: isDeleting }] = useDeleteContactMutation();

    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [occupationToDelete, setOccupationToDelete] = useState<ProjectUserOccupation | null>(null);

    const showSuccessMessage = (response: ContactResponse) => {
        dispatch(showSnackBar({ text: response.message, severity: 'success' }));
    };

    const handleRoleChange = (occupation: ProjectUserOccupation, newRoleId: number) => {
        if (newRoleId === occupation.role_id) {
            return;
        }
        modifyContact({
            project_id: projectId,
            user_id: occupation.contact.user_id,
            old_role_id: occupation.role_id,
            new_role_id: newRoleId,
        })
            .unwrap()
            .then(rejectNullResponse)
            .then((response) => {
                showSuccessMessage(response);
                setEditingKey(null);
            })
            .catch((error) => {
                dispatch(showSnackBar({
                    text: getV1ErrorMessage(error, ts('project_users_modify_error', 'Failed to modify the role')),
                    severity: 'error',
                }));
            });
    };

    const handleDeleteConfirm = () => {
        if (occupationToDelete == null) {
            return;
        }
        deleteContact({
            project_id: projectId,
            user_id: occupationToDelete.contact.user_id,
            role_id: occupationToDelete.role_id,
        })
            .unwrap()
            .then(rejectNullResponse)
            .then(showSuccessMessage)
            .catch((error) => {
                dispatch(showSnackBar({
                    text: getV1ErrorMessage(error, ts('project_users_delete_error', 'Failed to delete the contact')),
                    severity: 'error',
                }));
            })
            .finally(() => setOccupationToDelete(null));
    };

    const deleteAction = (occupation: ProjectUserOccupation) => canEdit && (
        <IconButton size="small" onClick={() => setOccupationToDelete(occupation)}>
            <DeleteIcon fontSize="small" />
        </IconButton>
    );

    const roleEditor = (occupation: ProjectUserOccupation) => (
        <>
            <Select
                size="small"
                value={occupation.role_id}
                onChange={(event) => handleRoleChange(occupation, Number(event.target.value))}
                disabled={isModifying}
            >
                {users.roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                ))}
            </Select>
            <Button size="small" onClick={() => setEditingKey(null)} disabled={isModifying}>
                {ts('plt_cancel', 'Cancel')}
            </Button>
        </>
    );

    const editButton = (occupation: ProjectUserOccupation) => canEdit && (
        <Tooltip title={ts('project_users_edit', 'Edit')}>
            <IconButton size="small" onClick={() => setEditingKey(occupationKey(occupation))}>
                <EditIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: DEFAULT_SPACING,
            }}
        >
            {users.owner && (
                <UserCard
                    title={ts('project_users_owner', 'Project Owner')}
                    avatarLink={users.owner.user_avatar_link}
                    rows={contactRows(users.owner)}
                />
            )}
            {users.occupations.map((occupation) => (
                isAccepted(occupation) ? (
                    <UserCard
                        key={occupationKey(occupation)}
                        title={editingKey === occupationKey(occupation) ? '' : occupation.role_name}
                        titleAction={editingKey === occupationKey(occupation)
                            ? roleEditor(occupation)
                            : editButton(occupation)}
                        actions={deleteAction(occupation)}
                        avatarLink={occupation.contact.user_avatar_link}
                        rows={contactRows(occupation.contact)}
                    />
                ) : (
                    <UserCard
                        key={occupationKey(occupation)}
                        title={pendingTitle(occupation)}
                        actions={deleteAction(occupation)}
                        avatarLink={occupation.contact.user_avatar_link}
                        rows={[
                            { label: ts('project_users_email', 'Email address'), value: occupation.contact.email },
                            { label: ts('project_users_occupation', 'Occupation'), value: occupation.role_name },
                        ]}
                    />
                )
            ))}
            {inviteSlot}
            <DeleteUserDialog
                open={occupationToDelete != null}
                disabled={isDeleting}
                onClose={() => setOccupationToDelete(null)}
                onConfirm={handleDeleteConfirm}
            />
        </Box>
    );
};
